import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { Icon } from '@/icons/Icon'
import { LogoMark } from '@/components/Logo'
import { useBodyClass } from '@/hooks/useBodyClass'

import { usePlatformAuth } from './auth'
import { isMfaChallenge, message, type PlatformMfaChallenge } from './api'

import '@/styles/pages/login.css'

/**
 * The portal's sign-in page, drawn with the application's own auth card.
 *
 * What it does not have is the point: no request access, no Microsoft or
 * Google button, no password reset link. None of them exist here - a platform
 * administrator is created by another one or by
 * `python -m app.create_platform_admin` on the server, and there is nobody
 * above them to mail a reset link.
 *
 * It says nothing about who may sign in. A carrier's own correct password gets
 * the same sentence a wrong one does (`app/api/v1/platform.py`), so the page
 * must not offer to help somebody work out which.
 *
 * **The statement gets half the screen.** The application's sign-in is a card
 * centred in an empty field, which is right for it: a carrier's person arrives
 * knowing where they are. The one thing this page has to say before anything
 * is typed is *which* product it is, and a line of grey text under a logo is
 * not a way to say it. The form is still the application's own, with its box
 * removed - the screen is already divided, so a second frame inside one half
 * is a border around a border.
 */

/** What this portal is. Three nouns, which are also its three pages. */
const COVERS = [
  { icon: 'layout-grid', label: 'Every carrier on this installation' },
  { icon: 'shield', label: 'Who may sign in to this portal' },
  { icon: 'clock', label: 'What was done here, and by whom' },
] as const

function BrandSide() {
  return (
    <aside className="pf-auth__brand">
      <span className="pf-auth__eyebrow">Insuronix platform</span>

      <div className="pf-auth__say">
        <h2 className="pf-auth__h">Platform administration</h2>
        <p className="pf-auth__sub">
          Separate from the application: its own host, its own sign-in, and no carrier&rsquo;s data.
        </p>

        <ul className="pf-auth__list">
          {COVERS.map((cover) => (
            <li key={cover.label}>
              <Icon name={cover.icon} size={16} />
              {cover.label}
            </li>
          ))}
        </ul>
      </div>

    </aside>
  )
}

export default function PlatformLoginPage() {
  useBodyClass('ix-body-login')
  useBodyClass('ix-body-platform')

  const { signIn, verifyMfa } = usePlatformAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Second step, only for accounts with two-factor on.
  const [challenge, setChallenge] = useState<PlatformMfaChallenge | null>(null)
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)

  useEffect(() => {
    document.title = 'Sign in · Insuronix Platform'
  }, [])

  const enter = useMutation({
    mutationFn: () => signIn(email.trim().toLowerCase(), password),
    onSuccess: (result) => {
      if (isMfaChallenge(result)) {
        setChallenge(result)
        setCode('')
        setUseRecovery(false)
      }
    },
  })

  const verify = useMutation({
    mutationFn: () =>
      verifyMfa(
        useRecovery
          ? { challenge: challenge?.challenge ?? '', recoveryCode: code.trim() }
          : { challenge: challenge?.challenge ?? '', code: code.replace(/\s/g, '') },
      ),
  })

  if (challenge) {
    return (
      <div className="pf-auth">
        <BrandSide />
        <div className="pf-auth__form">
          <main className="au-card">
            <div className="au-head">
              <div className="au-brand">
                <LogoMark variant="auth" />
              </div>
              <div className="au-sub">Two-factor authentication</div>
            </div>

            <form
              className="au-form"
              onSubmit={(e) => {
                e.preventDefault()
                if (verify.isPending || !code.trim()) return
                verify.mutate()
              }}
            >
              <div className="au-field">
                <div className="au-label-row">
                  <label className="au-label" htmlFor="pf-code">
                    {useRecovery ? 'Recovery code' : 'Authentication code'}
                  </label>
                  <button
                    className="au-link-sm"
                    type="button"
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    onClick={() => {
                      setUseRecovery((v) => !v)
                      setCode('')
                      verify.reset()
                    }}
                  >
                    {useRecovery ? 'Use your authenticator app' : 'Use a recovery code'}
                  </button>
                </div>
                <input
                  className="au-input"
                  id="pf-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={useRecovery ? 24 : 12}
                  autoFocus
                />
                <div className="ix-caption ix-text-muted">
                  {useRecovery
                    ? 'Each recovery code works once.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </div>
              </div>

              <button className="au-submit" type="submit" disabled={verify.isPending || !code.trim()}>
                {verify.isPending ? 'Verifying…' : 'Verify and sign in'}
              </button>
            </form>

            {verify.error ? (
              <div className="ix-error-text" role="alert">
                {message(verify.error, 'Could not sign in.')}
              </div>
            ) : null}

            <div className="au-alt">
              Not you?{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault()
                  setChallenge(null)
                  setPassword('')
                  setCode('')
                  enter.reset()
                  verify.reset()
                }}
              >
                Start over
              </a>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <div className="pf-auth">
      <BrandSide />
      <div className="pf-auth__form">
        <main className="au-card">
          <div className="au-head">
            <div className="au-brand">
              <LogoMark variant="auth" />
            </div>
            <div className="au-sub">Sign in to administer the platform</div>
          </div>

          <form
            className="au-form"
            onSubmit={(e) => {
              e.preventDefault()
              if (enter.isPending || !email.trim() || !password) return
              enter.mutate()
            }}
          >
            <div className="au-field">
              <label className="au-label" htmlFor="pf-email">
                Email
              </label>
              <input
                className="au-input"
                id="pf-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>

            <div className="au-field">
              <label className="au-label" htmlFor="pf-password">
                Password
              </label>
              <input
                className="au-input"
                id="pf-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <button
              className="au-submit"
              type="submit"
              disabled={enter.isPending || !email.trim() || !password}
            >
              {enter.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          {enter.error ? (
            <div className="ix-error-text" role="alert">
              {message(enter.error, 'Could not sign in.')}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  )
}
